import { call, delay, fork, put, race, select, take, takeEvery, takeLatest } from "redux-saga/effects";
import { registrationApi } from "../../../api/registration/registrationApi";
import { clearQueueToken } from "../../../helpers/queueToken";
import { NotifyHelper } from "../../../helpers/toast";
import { IBaseRespone } from "../../../model/respone/IBaseRespone";
import { IChotDuLieuResult } from "../../../model/respone/registration/IChotDuLieuResult";
import { IMyRegistration } from "../../../model/respone/registration/IMyRegistration";
import { IRegistrationResult } from "../../../model/respone/registration/IRegistrationResult";
import {
    eRegistrationActionTypeIds,
    IRegistrationActions,
} from "../../action-types/registration/IRegistrationActions";
import { rootActions } from "../../actions/rootActions";

/**
 * Sau khi backend trả 200 pending_confirmation, FE chờ SignalR `RegistrationConfirmed` để clear
 * pendingClassIds + show toast. Nếu Worker SETNX blip (RegistrationConsumer.cs:97-114 return sớm,
 * không fire SignalR) hoặc Hub disconnect mid-flight → SV kẹt spinner mãi.
 *
 * Timeout 15s là threshold giữa "Worker bình thường" (~50-300ms) và "Worker bị stall do RMQ retry"
 * (~5s/15s/30s/45s/50s nếu retry hết MassTransit policy). 15s đủ cover retry lần 1 (5s) + ack +
 * SignalR send. Nếu đến 15s vẫn pending → poll /my-registrations để reconcile từ DB.
 *
 * Tổng watchdog = PENDING_TIMEOUT_MS × MAX_ATTEMPTS = 15s × 5 = 75s, đủ cover MassTransit max
 * retry policy (5s/15s/30s ~ 50s) + buffer cho DB lag. Trước đây 2 attempts (30s) → SV thấy toast
 * "thất bại" trong khi Worker thật ra chỉ chậm → SV bấm Huỷ → 1-2s sau Worker insert xong → cancel
 * OK → SV gặp UX contradiction (toast fail + cancel success cùng 1 lớp).
 *
 * Từ attempt 2 (T=30s) chỉ show INFO toast "đang xử lý lâu hơn", KHÔNG fire REGISTER_FAILED.
 * Chỉ sau MAX_ATTEMPTS (T=75s) mới coi như fail thật và dispatch REGISTER_FAILED. Race với SignalR
 * RegistrationConfirmed/Failed vẫn short-circuit early ở mọi attempt.
 */
const PENDING_TIMEOUT_MS = 15000;
const PENDING_MAX_ATTEMPTS = 5;
const SLOW_NOTICE_AFTER_ATTEMPT = 2;

export function* registrationSaga(): any {
    // takeEvery cho register/cancel — SV cần đăng ký NHIỀU lớp song song (7 môn liên tiếp = normal).
    // Dedupe double-click cùng 1 classId NÊN làm ở UI level (disable button per row khi pending),
    // KHÔNG dedupe ở saga level (sẽ block legitimate register lớp khác cùng lúc).
    // Backend đã idempotent: TryReserve SISMEMBER guard → double POST cùng classId trả -2 ALREADY_REGISTERED.
    yield takeEvery(eRegistrationActionTypeIds.REGISTER_START, registerWorker);
    yield takeEvery(eRegistrationActionTypeIds.REGISTER_PAIR_START, registerPairWorker);
    yield takeEvery(eRegistrationActionTypeIds.CANCEL_START, cancelWorker);
    yield takeLatest(eRegistrationActionTypeIds.LOAD_MY_START, loadMyWorker);
    yield takeEvery(eRegistrationActionTypeIds.REGISTER_CONFIRMED, confirmedFromHubWorker);
    yield takeEvery(eRegistrationActionTypeIds.REGISTER_FAILED, failedFromHubWorker);
    // takeLatest: chống double-click bấm 2 lần button "Lưu". Operation idempotent ở backend nên
    // 2 call không gây side-effect, nhưng UX 2 toast là confusing — takeLatest hủy req trước.
    yield takeLatest(eRegistrationActionTypeIds.CHOT_DU_LIEU_START, chotDuLieuWorker);
}

/**
 * QueueAccessMiddleware trả 403 NOT_YOUR_TURN cho POST/DELETE /api/registration khi access token
 * reg:access hết hạn / bị thu hồi (TTL 10-15 phút trôi hết, đóng cổng, Redis reset, hoặc token cấp từ
 * thiết bị vào trước đã hết hạn). Trước đây FE chỉ hiện "Vui lòng chờ đến lượt." rồi kẹt — SV không có
 * đường quay lại. Ở đây: xoá token cũ + ẩn UI "đã có quyền" ngay + refetch /queue/status (server tự
 * re-enqueue nếu cổng còn mở). Trả true nếu đã xử lý → worker bỏ qua toast lỗi mặc định.
 */
function* handleQueueRejection(res: IBaseRespone<any>): any {
    if (res.status_code !== 403) return false;
    const reasonCode = (res.data as { reasonCode?: string } | undefined)?.reasonCode;
    if (reasonCode !== "NOT_YOUR_TURN") return false;

    clearQueueToken();
    NotifyHelper.Error("Phiên vào cổng đăng ký đã hết hạn. Bạn được đưa trở lại hàng chờ — vui lòng đợi đến lượt.");
    // Ẩn ngay trạng thái "đã có quyền" (chống cửa sổ vài giây UI còn cho thao tác dưới mạng chậm),
    // rồi refetch trạng thái thật — server cấp lại vị trí hàng chờ / token mới nếu cổng còn mở.
    yield put(rootActions.queue.SET_POSITION({ inQueue: true, hasAccess: false }));
    yield put(rootActions.queue.LOAD_STATUS_START(undefined));
    return true;
}

function* registerWorker(action: IRegistrationActions): any {
    const classId = (action.payload as { classId: number }).classId;
    const res: IBaseRespone<IRegistrationResult> = yield call(registrationApi.Register, classId);
    if (res.is_success && res.data) {
        yield put(rootActions.registration.REGISTER_PENDING({ classId }));
        yield fork(pendingTimeoutWorker, classId);
        NotifyHelper.Info("Đang chờ xác nhận đăng ký...");
    } else {
        const handled: boolean = yield call(handleQueueRejection, res);
        if (!handled) NotifyHelper.Error(res.message || "Đăng ký thất bại.");
        yield put(rootActions.registration.REGISTER_ERROR({ classId, message: res.message || "" }));
    }
}

/**
 * Phase 2C: đăng ký atomic 2 lớp parent + child.
 * Backend reserve cả 2 slot Lua atomic → publish 2 events độc lập.
 * Frontend: dispatch REGISTER_PENDING cho cả 2 classId để UI hiển thị spinner trên cả 2 row.
 */
function* registerPairWorker(action: IRegistrationActions): any {
    const { parentClassId, childClassId } = action.payload as { parentClassId: number; childClassId: number };
    const res: IBaseRespone<IRegistrationResult> = yield call(
        registrationApi.RegisterPair, parentClassId, childClassId
    );
    if (res.is_success && res.data) {
        yield put(rootActions.registration.REGISTER_PENDING({ classId: parentClassId }));
        yield put(rootActions.registration.REGISTER_PENDING({ classId: childClassId }));
        yield fork(pendingTimeoutWorker, parentClassId);
        yield fork(pendingTimeoutWorker, childClassId);
        NotifyHelper.Info("Đang chờ xác nhận đăng ký 2 lớp...");
    } else {
        const handled: boolean = yield call(handleQueueRejection, res);
        if (!handled) NotifyHelper.Error(res.message || "Đăng ký cặp lớp thất bại.");
        yield put(rootActions.registration.REGISTER_ERROR({ classId: parentClassId, message: res.message || "" }));
        yield put(rootActions.registration.REGISTER_ERROR({ classId: childClassId, message: res.message || "" }));
    }
}

function* cancelWorker(action: IRegistrationActions): any {
    const classId = (action.payload as { classId: number }).classId;
    const res: IBaseRespone<IRegistrationResult> = yield call(registrationApi.Cancel, classId);
    if (res.is_success) {
        // Huỷ cặp LT-TH: backend trả cancelledClassIds gồm cả lớp bạn → restore slot/canRegister cả 2.
        const cancelledClassIds = res.data?.cancelledClassIds ?? [classId];
        NotifyHelper.Success(cancelledClassIds.length > 1 ? "Đã hủy đăng ký (cả lý thuyết + thực hành)." : "Đã hủy đăng ký.");
        yield put(rootActions.registration.CANCEL_SUCCESS({ classId, cancelledClassIds }));
        yield put(rootActions.registration.LOAD_MY_START(undefined));
        // Refresh financial — tong_tien_phai_nop giảm sau cancel → header "Còn lại" + FE gate
        // getFinancialBlock update ngay, không phải đợi poll 60s. Saga financial dùng takeLatest
        // nên không double-poll với vòng 60s đang chạy.
        yield put(rootActions.financial.LOAD_START());
        // Slot count cột "Còn trống" được optimistic-update ngay tại classesReducer (nghe REG_CANCEL_SUCCESS)
        // → KHÔNG cần dispatch LOAD_START refetch full list (tiết kiệm 350k GET calls/peak).
    } else {
        const handled: boolean = yield call(handleQueueRejection, res);
        if (!handled) NotifyHelper.Error(res.message || "Hủy đăng ký thất bại.");
        yield put(rootActions.registration.CANCEL_ERROR({ classId, message: res.message || "" }));
    }
}

function* loadMyWorker(): any {
    const res: IBaseRespone<IMyRegistration[]> = yield call(registrationApi.GetMy);
    if (res.is_success && res.data) {
        yield put(rootActions.registration.LOAD_MY_SUCCESS(res.data));
    } else {
        yield put(rootActions.registration.LOAD_MY_ERROR(res.message || "Không tải được danh sách."));
    }
}

function* confirmedFromHubWorker(action: IRegistrationActions): any {
    const payload = action.payload as { classId: number };
    NotifyHelper.Success(`Đăng ký lớp #${payload.classId} thành công!`);
    yield put(rootActions.registration.LOAD_MY_START(undefined));
    // Refresh financial — tong_tien_phai_nop tăng sau register → header "Còn lại" + FE gate
    // getFinancialBlock update ngay, không phải đợi poll 60s. Cover cả 2 path đến đây:
    // SignalR Hub (Worker confirm) + pendingTimeoutWorker poll-fallback (dispatch REGISTER_CONFIRMED
    // sau khi DB poll thấy row).
    yield put(rootActions.financial.LOAD_START());
    // Slot count cột "Còn trống" được optimistic-update ngay tại classesReducer (nghe REG_REGISTER_CONFIRMED)
    // → KHÔNG cần dispatch LOAD_START refetch full list (tiết kiệm ~350k GET calls/peak).
}

function* failedFromHubWorker(action: IRegistrationActions): any {
    const payload = action.payload as { classId: number; reason?: string };
    yield call(NotifyHelper.Error, `Đăng ký lớp #${payload.classId} thất bại: ${payload.reason || "Vui lòng thử lại."}`);
}

function* chotDuLieuWorker(): any {
    const res: IBaseRespone<IChotDuLieuResult> = yield call(registrationApi.ChotDuLieu);
    if (res.is_success && res.data) {
        NotifyHelper.Success("Lưu dữ liệu đăng ký thành công!");
        yield put(rootActions.registration.CHOT_DU_LIEU_SUCCESS({
            ngayChotDuLieu: res.data.ngayChotDuLieu,
            classIds: res.data.classIds,
        }));
        // Reload từ DB để đồng bộ (edge case: row vừa register sau khi user click chốt cũng được khoá ở SP).
        yield put(rootActions.registration.LOAD_MY_START(undefined));
    } else {
        const handled: boolean = yield call(handleQueueRejection, res);
        if (!handled) NotifyHelper.Error(res.message || "Lưu dữ liệu thất bại.");
        yield put(rootActions.registration.CHOT_DU_LIEU_ERROR(res.message || ""));
    }
}

/**
 * Watchdog cho 1 classId pending. Mỗi attempt 15s → poll /my-registrations:
 *   - DB có row trang_thai=1 → REGISTER_CONFIRMED, exit.
 *   - DB chưa có → spawn attempt tiếp theo cho đến PENDING_MAX_ATTEMPTS.
 *   - Tại attempt SLOW_NOTICE_AFTER_ATTEMPT (30s): show INFO toast 1 lần để SV biết là chậm chứ
 *     KHÔNG phải fail — Worker có thể đang retry MassTransit (max ~50s).
 *   - Sau MAX_ATTEMPTS attempts (75s) vẫn không có → REGISTER_FAILED (thật).
 *
 * Race với các action resolution để exit sớm khi:
 *   - SignalR đến đúng giờ (CONFIRMED/FAILED qua hub)
 *   - SV chủ động cancel mid-pending (CANCEL_SUCCESS)
 *   - Backend response error sau cùng (REGISTER_ERROR — hiếm vì error trả ngay khi POST)
 */
function* pendingTimeoutWorker(classId: number, attempt = 1): any {
    // Fast-path guard: SignalR `RegistrationConfirmed` có thể về TRƯỚC khi watchdog kịp fork +
    // đăng ký `take(isResolution)` (localhost: Worker consume→insert→SignalR nhanh hơn cả round-trip
    // HTTP của POST /register). `take` chỉ bắt action TƯƠNG LAI nên miss confirmed đã bắn → timeout
    // 15s → poll → re-dispatch REGISTER_CONFIRMED → toast "thành công" TRÙNG ở giây 15.
    // confirmedClassIds đã chứa classId ⇒ đường thật đã xong → thoát ngay, không poll, không toast lại.
    const already: boolean = yield select((s: any) => s.registration.confirmedClassIds.includes(classId));
    if (already) return;

    const isResolution = (a: any) => {
        if (!a || !a.payload) return false;
        const t = a.type;
        const cid = a.payload.classId;
        if (cid !== classId) return false;
        return (
            t === eRegistrationActionTypeIds.REGISTER_CONFIRMED ||
            t === eRegistrationActionTypeIds.REGISTER_FAILED ||
            t === eRegistrationActionTypeIds.REGISTER_ERROR ||
            t === eRegistrationActionTypeIds.CANCEL_SUCCESS
        );
    };

    const result: { timeout?: true; resolved?: any } = yield race({
        resolved: take(isResolution),
        timeout: delay(PENDING_TIMEOUT_MS),
    });
    if (!result.timeout) return;

    // Re-check trước khi poll: confirmed có thể vừa tới trong cửa sổ chờ nhưng bị race với timeout
    // (hoặc REGISTER_CONFIRMED về trước REGISTER_PENDING nên isResolution không khớp lần này).
    const confirmedNow: boolean = yield select((s: any) => s.registration.confirmedClassIds.includes(classId));
    if (confirmedNow) return;

    // Hub không reach trong 15s → poll DB.
    yield put(rootActions.registration.LOAD_MY_START(undefined));
    const loaded: IRegistrationActions = yield take(eRegistrationActionTypeIds.LOAD_MY_SUCCESS);
    const myList = (loaded.payload as IMyRegistration[]) || [];
    const confirmedRow = myList.find(r => r.id_lop_tc === classId && r.trang_thai === 1);

    if (confirmedRow) {
        // DB đã có row → giả lập SignalR CONFIRMED để clear pendingClassIds + chạy
        // confirmedFromHubWorker (toast + reload optimistic).
        yield put(rootActions.registration.REGISTER_CONFIRMED({
            registrationId: confirmedRow.id,
            classId,
            status: "confirmed",
        }));
        return;
    }

    // DB chưa có row. Có thể Worker đang retry MassTransit (5s/15s/30s ~ 50s).
    if (attempt < PENDING_MAX_ATTEMPTS) {
        // Tại attempt SLOW_NOTICE_AFTER_ATTEMPT chỉ show INFO 1 lần — KHÔNG fire REGISTER_FAILED.
        // SV thấy thông báo "đang xử lý lâu" thay vì "thất bại" sớm gây hiểu nhầm.
        if (attempt === SLOW_NOTICE_AFTER_ATTEMPT) {
            NotifyHelper.Info(
                `Lớp #${classId} đang được xác nhận lâu hơn dự kiến. Hệ thống sẽ tự cập nhật khi xong.`
            );
        }
        yield fork(pendingTimeoutWorker, classId, attempt + 1);
        return;
    }

    // Sau PENDING_MAX_ATTEMPTS × 15s = 75s vẫn không có row → coi như fail thật. Clear pending để
    // SV thử lại. Tới đây MassTransit đã hết retry policy → nếu thật sự fail, DLC đã fire
    // RegistrationFailed qua SignalR và race đã exit trước.
    yield put(rootActions.registration.REGISTER_FAILED({
        classId,
        reason: "Không nhận được xác nhận từ hệ thống sau ~75 giây. Vui lòng kiểm tra lại danh sách đăng ký hoặc thử lại.",
    }));
}
